import React, { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { fetchWithAuth } from "../../../../../api-client/APIClient";
import {
  GUARDRAILS_ENFORCED_STATUSES,
  LinkedAccount,
  UserType,
} from "../../../../../models/Entities";
import {
  navigateToProductionLinkedAccountsTablePage,
  navigateToTestLinkedAccountsTablePage,
} from "../../../../../router/RouterUtils";
import useAppContext from "../../../../context/useAppContext";
import EmptyStateWrapper from "../../../../shared/EmptyStateWrapper";
import { showErrorToast, showSuccessToast } from "../../../../shared/Toasts";
import LinkedAccountDeleteSwitch from "../../shared/LinkedAccountDeleteSwitch";
import LinkedAccountDeletionModal from "../../shared/LinkedAccountDeletionModal";
import LinkedAccountTypeConvertButton from "./LinkedAccountTypeConvertButton";
import { Card, CopyTextField, Text } from "@merge-api/merge-javascript-shared";

type Props = {
  /**
   * Something to call when the linked account updates
   */
  updateLinkedAccount: (linkedAccount: LinkedAccount) => void;
  linkedAccount: LinkedAccount | null;
};

/**
 * Shows a few settings for the linked account itself. Always guaranteed to have content if there's a linked account
 */
const SettingsPage = ({ linkedAccount, updateLinkedAccount }: Props) => {
  const { user, isUserPrivileged } = useAppContext();
  const priviligedRoles = new Set([
    UserType.admin_with_billing,
    UserType.admin_without_billing,
    UserType.member,
  ]);
  const history = useHistory();
  const isTestAccount = useMemo(() => linkedAccount?.is_test_account ?? false, [linkedAccount]);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const deleteAccount = useCallback(() => {
    if (!linkedAccount) {
      return;
    }
    let isCancelled = false;
    fetchWithAuth({
      path: `/integrations/linked-accounts/${linkedAccount.id}`,
      method: "DELETE",
      onResponse: () => {
        if (isCancelled) {
          return;
        }
        setShowDeletionModal(false);
        showSuccessToast(`Deleted linked account!`);
        if (isTestAccount) {
          navigateToTestLinkedAccountsTablePage(history);
        } else {
          navigateToProductionLinkedAccountsTablePage(history);
        }
        updateLinkedAccount(linkedAccount);
      },
      onError: () => {
        if (isCancelled) {
          return;
        }
        showErrorToast("Failed to delete linked account. Please try again.");
      },
    });
    return () => {
      isCancelled = true;
    };
  }, [linkedAccount]);

  // Adds both the confirm modal and the card to delete
  const deleteAccountContent = linkedAccount && priviligedRoles.has(user.type) && (
    <>
      <LinkedAccountDeleteSwitch
        linkedAccount={linkedAccount}
        onClick={() => setShowDeletionModal(true)}
      />
      <LinkedAccountDeletionModal
        isModalOpen={showDeletionModal}
        deleteLinkedAccount={deleteAccount}
        closeModal={() => setShowDeletionModal(false)}
        linkedAccount={linkedAccount}
      />
    </>
  );

  const linkedAccountInfoContent = linkedAccount && (
    <Card className="flex flex-col">
      <div className="border-b border-gray-10 p-5">
        <Text variant="h4">Account details</Text>
      </div>
      {/* End-user origin ID and email are PII identifiers and are thus masked at the section */}
      <div className="ph-no-capture flex flex-col gap-y-6 p-5">
        <div className="flex flex-col gap-y-1">
          <Text variant="h6">End user origin ID</Text>
          <CopyTextField text={linkedAccount?.end_user.origin_id} />
        </div>
        <div className="flex flex-col gap-y-1">
          <Text variant="h6">Email</Text>
          <CopyTextField text={linkedAccount?.end_user.email_address} />
        </div>
      </div>
    </Card>
  );

  const guardrailsEnforced =
    user.organization.billing_guardrails_status !== undefined &&
    GUARDRAILS_ENFORCED_STATUSES.includes(user.organization.billing_guardrails_status);
  return (
    <div className="flex flex-col gap-y-6 pb-8">
      {linkedAccount && isTestAccount && isUserPrivileged && (
        <>
          <LinkedAccountTypeConvertButton
            linkedAccount={linkedAccount}
            updateLinkedAccount={updateLinkedAccount}
            guardrailsEnforced={guardrailsEnforced}
          />
        </>
      )}
      {linkedAccountInfoContent}
      {deleteAccountContent}
      {!linkedAccount && <EmptyStateWrapper title="No settings" />}
    </div>
  );
};

export default SettingsPage;
